import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Typography, Tag, Spin, Alert, Button, Empty, Space, Input, Select,
  Statistic, Table, Tooltip, Switch, InputNumber, Slider, Drawer,
  Pagination, Modal, Row, Col, Card, Progress, Divider, Tabs, message
} from 'antd';
import {
  DatabaseOutlined, ReloadOutlined, SearchOutlined,
  PlayCircleOutlined, PauseCircleOutlined, CaretRightOutlined,
  HistoryOutlined, SyncOutlined, CheckCircleOutlined,
  CloseCircleOutlined, ClockCircleOutlined, DashboardOutlined,
  SettingOutlined, ThunderboltOutlined, SortAscendingOutlined,
  CloudServerOutlined, ApiOutlined, FileSearchOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import {
  getGalleryInsights,
  getGalleryIndexerStatus, getGalleryIndexerHistory,
  triggerGalleryIndexer, pauseGalleryIndexer, resumeGalleryIndexer,
  getGalleryRagConfig, updateGalleryRagConfig,
  getGalleryScheduleConfig, updateGalleryScheduleConfig,
  triggerGalleryVectorRebuild,
  executeSql,
  getDataQAHistory, getDataQAStatistics,
  submitDataFeedback, markDataQAAnswer
} from '../services/dataService';
import QAHistory from './QAHistory';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import vegaEmbed from 'vega-embed';
import dayjs from 'dayjs';
import PlotlyFigureView, { isPlotlyFigure } from '../components/PlotlyFigureView';
import './DataGallery.css';

const { Title, Text } = Typography;
const PAGE_SIZE = 20;

/* ── Sample data generator for empty chart specs ──────────── */
function generateSampleData(spec) {
  if (!spec) return null;
  const parsed = typeof spec === 'string' ? JSON.parse(spec) : { ...spec };
  // If spec already has inline data, return as-is
  if (parsed.data?.values?.length > 0) return parsed;
  // Detect mark type
  const mark = typeof parsed.mark === 'string' ? parsed.mark : parsed.mark?.type;
  const enc = parsed.encoding || {};
  const xField = enc.x?.field || 'category';
  const yField = enc.y?.field || 'value';
  const colorField = enc.color?.field;

  const categories = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  let values = [];

  if (mark === 'arc' || mark === 'pie') {
    const labels = ['Segment A', 'Segment B', 'Segment C', 'Segment D'];
    values = labels.map((l, i) => ({ [xField]: l, [yField]: [35, 28, 22, 15][i] }));
  } else if (mark === 'line' || mark === 'area') {
    if (colorField) {
      ['Series A', 'Series B'].forEach(s =>
        categories.forEach((c, i) => values.push({
          [xField]: c, [yField]: Math.round(20 + Math.sin(i + (s === 'Series A' ? 0 : 2)) * 15 + Math.random() * 10), [colorField]: s
        }))
      );
    } else {
      values = categories.map((c, i) => ({ [xField]: c, [yField]: Math.round(30 + Math.sin(i) * 20 + Math.random() * 10) }));
    }
  } else {
    // bar, point, rect, etc.
    values = categories.map((c, i) => ({ [xField]: c, [yField]: Math.round(15 + i * 8 + Math.random() * 12) }));
  }

  return { ...parsed, data: { values } };
}

/* ── Tiny chart thumbnail (renders a Vega-Lite spec) ──────── */
function ChartThumbnail({ spec }) {
  const ref = useRef(null);
  const parsed = (() => {
    if (!spec) return null;
    try { return typeof spec === 'string' ? JSON.parse(spec) : spec; } catch { return null; }
  })();
  const isPlotly = parsed && isPlotlyFigure(parsed);
  useEffect(() => {
    if (isPlotly) return undefined;
    if (!ref.current || !parsed) return undefined;
    let cancelled = false;
    try {
      const withData = generateSampleData(parsed);
      if (!withData) return undefined;
      const thumbSpec = { ...withData, width: 220, height: 110, autosize: 'fit', padding: 4 };
      delete thumbSpec.selection;
      // Remove title for compact thumbnail
      delete thumbSpec.title;
      vegaEmbed(ref.current, thumbSpec, {
        actions: false, renderer: 'svg', tooltip: false
      }).then(result => { if (cancelled) result.view.finalize(); })
        .catch(() => {});
    } catch { /* ignore bad specs */ }
    return () => { cancelled = true; };
  }, [parsed, isPlotly]);
  if (isPlotly) {
    return (
      <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
        <PlotlyFigureView
          figure={parsed}
          fill
          layoutOverride={{
            margin: { t: 4, r: 4, b: 16, l: 24 },
            showlegend: false,
            title: undefined,
          }}
        />
      </div>
    );
  }
  return <div ref={ref} style={{ width: '100%', height: '100%' }} />;
}

/* ── Full chart in drawer ──────────────────────────────────── */
function DrawerChart({ spec }) {
  const ref = useRef(null);
  const parsed = (() => {
    if (!spec) return null;
    try { return typeof spec === 'string' ? JSON.parse(spec) : spec; } catch { return null; }
  })();
  const isPlotly = parsed && isPlotlyFigure(parsed);
  useEffect(() => {
    if (isPlotly) return;
    if (!ref.current || !parsed) return;
    try {
      const withData = generateSampleData(parsed);
      if (!withData) return;
      const full = { ...withData, width: 'container', height: 300 };
      vegaEmbed(ref.current, full, {
        actions: { export: true, source: false, compiled: false, editor: false }
      }).catch(() => {});
    } catch { /* ignore */ }
  }, [parsed, isPlotly]);
  if (isPlotly) {
    return (
      <div className="data-gallery__drawer-chart">
        <PlotlyFigureView figure={parsed} fill layoutOverride={{ height: 300 }} />
      </div>
    );
  }
  return <div ref={ref} className="data-gallery__drawer-chart" />;
}

/* ── Sample table thumbnail for table/metric types ─────────── */
function parseInsightData(insight) {
  // Try multiple data locations
  const sources = [
    insight?.delivery?.data,
    insight?.fetched_data,
  ];
  for (const src of sources) {
    if (!src) continue;
    try {
      const parsed = typeof src === 'string' ? JSON.parse(src) : src;
      const cols = parsed?.columns || (parsed?.rows?.[0] ? Object.keys(parsed.rows[0]) : null);
      const rows = parsed?.rows;
      if (cols && cols.length > 0 && rows && rows.length > 0) return { cols, rows };
      if (cols && cols.length > 0) return { cols, rows: [] };
    } catch { /* ignore */ }
  }
  // Try chart_config data.values
  const chartSpec = getChartSpec(insight);
  if (chartSpec) {
    try {
      const parsed = typeof chartSpec === 'string' ? JSON.parse(chartSpec) : chartSpec;
      if (parsed.data?.values?.length > 0) {
        const rows = parsed.data.values;
        const cols = Object.keys(rows[0]);
        return { cols, rows };
      }
    } catch { /* ignore */ }
  }
  return null;
}

const SAMPLE_TABLE_DATA = {
  cols: ['Category', 'Value', 'Change'],
  rows: [
    { 'Category': 'Product A', 'Value': '1,234', 'Change': '+12%' },
    { 'Category': 'Product B', 'Value': '987', 'Change': '+8%' },
    { 'Category': 'Product C', 'Value': '756', 'Change': '-3%' },
  ]
};

function TableThumbnail({ insight }) {
  const parsed = parseInsightData(insight);
  const { cols, rows } = parsed || SAMPLE_TABLE_DATA;
  const isSample = !parsed;

  return (
    <div className="data-gallery__mini-table">
      <table>
        <thead><tr>{cols.slice(0, 4).map((c, i) => <th key={i}>{c}</th>)}</tr></thead>
        <tbody>
          {(rows.length > 0 ? rows : SAMPLE_TABLE_DATA.rows).slice(0, 3).map((r, ri) => (
            <tr key={ri}>{cols.slice(0, 4).map((c, ci) => <td key={ci}>{String(r[c] ?? '')}</td>)}</tr>
          ))}
        </tbody>
      </table>
      <div className="data-gallery__mini-table-more">
        {isSample ? 'Sample data — click ▶ Run to load' : cols.length > 4 ? `+${cols.length - 4} more columns` : `${rows.length} rows`}
      </div>
    </div>
  );
}

/* ── Metric thumbnail ──────────────────────────────────────── */
function MetricThumbnail({ insight }) {
  const data = insight?.delivery?.data || insight?.fetched_data;
  let value = null;
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    const firstRow = parsed?.rows?.[0];
    if (firstRow) {
      const keys = Object.keys(firstRow);
      value = firstRow[keys[0]];
    }
  } catch { /* ignore */ }
  return (
    <div className="data-gallery__metric-thumb">
      <span className="data-gallery__metric-value">{value != null ? String(value) : '1,234'}</span>
      <span className="data-gallery__metric-label">{insight.title || insight.question || 'Metric'}</span>
    </div>
  );
}

/* ── Helper: determine chart spec from insight ─────────────── */
function getChartSpec(insight) {
  return insight?.chart_config || insight?.delivery?.chart || null;
}

/* ── Infer component type from insight data ────────────────── */
function inferComponentType(insight) {
  // component_type doesn't exist in the DB, so we infer it
  const chart = getChartSpec(insight);
  if (chart) {
    try {
      const parsed = typeof chart === 'string' ? JSON.parse(chart) : chart;
      // Plotly figure: look at first trace's type
      if (isPlotlyFigure(parsed)) {
        const t0 = parsed.data && parsed.data[0];
        const ttype = t0?.type;
        if (ttype === 'bar') return 'bar_chart';
        if (ttype === 'pie') return 'pie_chart';
        if (ttype === 'scatter') {
          const mode = t0?.mode || '';
          if (t0?.fill) return 'area_chart';
          if (mode.includes('lines')) return 'line_chart';
          if (mode.includes('markers')) return 'scatter';
          return 'line_chart';
        }
        return 'chart';
      }
      // Legacy Vega-Lite
      const mark = typeof parsed.mark === 'string' ? parsed.mark : parsed.mark?.type;
      if (mark === 'bar') return 'bar_chart';
      if (mark === 'line') return 'line_chart';
      if (mark === 'arc' || mark === 'pie') return 'pie_chart';
      if (mark === 'area') return 'area_chart';
      if (mark === 'point' || mark === 'circle') return 'scatter';
      return 'chart';
    } catch { return 'chart'; }
  }
  // Check if it has SQL + data but no chart → table or metric
  const sql = insight?.delivery?.sql || insight?.sql_query || insight?.generated_sql;
  if (sql) {
    const data = insight?.delivery?.data || insight?.fetched_data;
    if (data) {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        const cols = parsed?.columns || (parsed?.rows?.[0] ? Object.keys(parsed.rows[0]) : []);
        const rows = parsed?.rows || [];
        // Single value = metric
        if (rows.length === 1 && cols.length === 1) return 'metric';
        // Multiple rows or multiple columns = table
        if (rows.length > 0 || cols.length > 0) return 'table';
      } catch { /* ignore */ }
    }
    // Has SQL but no data yet → could be table
    return 'table';
  }
  return null;
}

/* ── Source label helpers ──────────────────────────────────── */
const SOURCE_META = {
  verified_answer:     { color: 'green',   icon: '✅', label: 'Verified',  full: 'Verified Answer' },
  dashboard_component: { color: 'blue',    icon: '📊', label: 'Dashboard', full: 'Dashboard Component' },
  manual:              { color: 'default', icon: '📝', label: 'Manual',    full: 'Manual' },
};
function sourceMeta(type) { return SOURCE_META[type] || SOURCE_META.manual; }

/* ═══════════════════════════════════════════════════════════ */
function DataGallery({ userRole }) {
  const VALID_TABS = ['insights', 'rag-setting', 'qa-history'];
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'insights';
  const setActiveTab = (key) => setSearchParams({ tab: key }, { replace: true });

  // Gallery state
  const [loading, setLoading] = useState(false);
  const [galleryData, setGalleryData] = useState(null);
  const [error, setError] = useState(null);
  const [filterKeyword, setFilterKeyword] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [currentPage, setCurrentPage] = useState(1);

  // Drawer
  const [drawerInsight, setDrawerInsight] = useState(null);

  // Indexer state
  const [indexerStatus, setIndexerStatus] = useState(null);
  const [indexerHistory, setIndexerHistory] = useState([]);
  const [indexerLoading, setIndexerLoading] = useState(false);
  const [vectorRebuilding, setVectorRebuilding] = useState(false);

  // RAG config state
  const [ragConfig, setRagConfig] = useState(null);
  const [ragSaving, setRagSaving] = useState(false);

  // Schedule config state
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const isAdmin = userRole?.toLowerCase() === 'admin';

  /* ── Data loading ──────────────────────────────────────── */
  useEffect(() => {
    loadGallery();
    if (isAdmin) {
      loadIndexerStatus();
      loadIndexerHistory();
      loadRagConfig();
      loadScheduleConfig();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGallery = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getGalleryInsights();
      setGalleryData(data);
    } catch (err) {
      setError('Failed to load gallery data: ' + (err.message || 'Unknown error'));
      message.error('Failed to load gallery data');
    } finally {
      setLoading(false);
    }
  };

  const loadIndexerStatus = async () => {
    try { setIndexerStatus(await getGalleryIndexerStatus()); } catch {}
  };
  const loadIndexerHistory = async () => {
    try { const d = await getGalleryIndexerHistory(20); setIndexerHistory(d?.runs || []); } catch {}
  };
  const loadRagConfig = async () => {
    try { setRagConfig(await getGalleryRagConfig()); } catch {}
  };
  const loadScheduleConfig = async () => {
    try { setScheduleConfig(await getGalleryScheduleConfig()); } catch {}
  };

  /* ── Admin actions ────────────────────────────────────── */
  const handleRunNow = async () => {
    setIndexerLoading(true);
    try {
      await triggerGalleryIndexer();
      message.success('Gallery index job triggered');
      setTimeout(() => { loadIndexerStatus(); loadIndexerHistory(); loadGallery(); }, 2000);
    } catch (err) {
      message.error('Failed to trigger index job: ' + (err.message || 'Unknown error'));
    } finally { setIndexerLoading(false); }
  };

  const handlePauseResume = async () => {
    setIndexerLoading(true);
    try {
      if (indexerStatus?.is_paused) { await resumeGalleryIndexer(); message.success('Indexer resumed'); }
      else { await pauseGalleryIndexer(); message.success('Indexer paused'); }
      await loadIndexerStatus();
    } catch (err) { message.error('Failed: ' + (err.message || 'Unknown error')); }
    finally { setIndexerLoading(false); }
  };

  const handleSaveRagConfig = async (updates) => {
    setRagSaving(true);
    try { setRagConfig(await updateGalleryRagConfig(updates)); message.success('RAG configuration saved'); }
    catch (err) { message.error('Failed to save RAG config: ' + (err.message || 'Unknown error')); }
    finally { setRagSaving(false); }
  };

  const handleSaveScheduleConfig = async (intervalSeconds) => {
    setScheduleSaving(true);
    try {
      setScheduleConfig(await updateGalleryScheduleConfig({ interval_seconds: intervalSeconds }));
      message.success('Schedule updated');
      loadIndexerStatus();
    } catch (err) { message.error('Failed to save schedule: ' + (err.message || 'Unknown error')); }
    finally { setScheduleSaving(false); }
  };

  const handleVectorRebuild = async () => {
    setVectorRebuilding(true);
    try {
      await triggerGalleryVectorRebuild();
      message.success('Vector index rebuild triggered');
      setTimeout(() => { loadIndexerStatus(); setVectorRebuilding(false); }, 2000);
    } catch (err) {
      message.error('Failed to trigger vector rebuild: ' + (err.message || 'Unknown error'));
      setVectorRebuilding(false);
    }
  };

  /* ── Filtering + sorting + pagination ──────────────────── */
  const filteredAndSorted = useMemo(() => {
    if (!galleryData?.insights) return [];
    let list = galleryData.insights.filter(i => {
      const kw = filterKeyword.toLowerCase();
      const matchKw = !kw
        || i.title?.toLowerCase().includes(kw)
        || i.question?.toLowerCase().includes(kw)
        || i.answer?.toLowerCase().includes(kw);
      const matchType = filterType === 'all'
        || inferComponentType(i) === filterType
        || i.source_type === filterType;
      return matchKw && matchType;
    });
    // sort
    list = [...list];
    switch (sortBy) {
      case 'date_desc': list.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || '')); break;
      case 'date_asc':  list.sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || '')); break;
      case 'title_asc': list.sort((a, b) => (a.title || a.question || '').localeCompare(b.title || b.question || '')); break;
      case 'type':      list.sort((a, b) => (a.source_type || '').localeCompare(b.source_type || '')); break;
      default: break;
    }
    return list;
  }, [galleryData, filterKeyword, filterType, sortBy]);

  // Reset page when filters change
  useEffect(() => { setCurrentPage(1); }, [filterKeyword, filterType, sortBy]);

  const pagedInsights = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAndSorted.slice(start, start + PAGE_SIZE);
  }, [filteredAndSorted, currentPage]);

  const totalCount = galleryData?.insights?.length || 0;

  /* ═══ RENDER ═══════════════════════════════════════════ */

  /* ── Tab: All Insights ── */
  const renderInsightsTab = () => (
    <>
      {/* Error */}
      {error && (
        <Alert message="Error" description={error} type="error" showIcon closable
          onClose={() => setError(null)} style={{ marginBottom: 16 }} />
      )}

      {/* Toolbar */}
      <div className="data-gallery__toolbar">
        <Input
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder="Search by title, question or answer..."
          value={filterKeyword}
          onChange={(e) => setFilterKeyword(e.target.value)}
          allowClear
          style={{ maxWidth: 360 }}
        />
        <Select
          value={filterType}
          onChange={setFilterType}
          style={{ minWidth: 180 }}
          options={[
            { label: 'All Types', value: 'all' },
            { label: '✅ Verified Answers', value: 'verified_answer' },
            { label: '📊 Dashboard Components', value: 'dashboard_component' },
            { label: '📝 Manual', value: 'manual' },
            { label: 'Metric', value: 'metric' },
            { label: 'Bar Chart', value: 'bar_chart' },
            { label: 'Line Chart', value: 'line_chart' },
            { label: 'Pie Chart', value: 'pie_chart' },
            { label: 'Area Chart', value: 'area_chart' },
            { label: 'Table', value: 'table' },
          ]}
        />
        <div className="data-gallery__toolbar-right">
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ minWidth: 160 }}
            suffixIcon={<SortAscendingOutlined />}
            options={[
              { label: 'Newest First', value: 'date_desc' },
              { label: 'Oldest First', value: 'date_asc' },
              { label: 'Title A–Z', value: 'title_asc' },
              { label: 'Group by Type', value: 'type' },
            ]}
          />
          <span className="data-gallery__count">
            {filteredAndSorted.length} of {totalCount} items
          </span>
        </div>
      </div>

      {/* Card Grid */}
      <Spin spinning={loading} tip="Loading gallery...">
        {!loading && filteredAndSorted.length === 0 ? (
          <div className="data-gallery__empty">
            <Empty
              description={galleryData ? 'No insights match your filters' : 'No gallery data loaded'}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        ) : (
          <>
            <div className="data-gallery__grid">
              {pagedInsights.map((insight, idx) => {
                const chart = getChartSpec(insight);
                const cType = inferComponentType(insight);
                const meta = sourceMeta(insight.source_type);
                const hasSql = !!(insight.delivery?.sql || insight.sql_query || insight.generated_sql);
                const thumbClass = chart ? 'data-gallery__card-thumb--chart'
                  : cType === 'metric' ? 'data-gallery__card-thumb--metric'
                  : cType === 'table' ? 'data-gallery__card-thumb--table'
                  : meta.icon === '📝' ? 'data-gallery__card-thumb--manual'
                  : 'data-gallery__card-thumb--qa';
                return (
                  <div
                    key={insight.id || idx}
                    className="data-gallery__card"
                    onClick={() => setDrawerInsight(insight)}
                  >
                    {/* Thumbnail */}
                    <div className={`data-gallery__card-thumb ${thumbClass}`}>
                      <Tag className="data-gallery__card-badge" color={meta.color}>
                        {meta.icon} {meta.label}
                      </Tag>
                      {/* Run button top-right */}
                      {hasSql && (
                        <Tooltip title="Run with live data">
                          <Button
                            type="primary"
                            size="small"
                            shape="circle"
                            className="data-gallery__card-run-btn"
                            icon={<PlayCircleOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDrawerInsight({ ...insight, _autoRun: true });
                            }}
                          />
                        </Tooltip>
                      )}
                      {chart ? (
                        <ChartThumbnail spec={chart} />
                      ) : cType === 'metric' ? (
                        <MetricThumbnail insight={insight} />
                      ) : cType === 'table' ? (
                        <TableThumbnail insight={insight} />
                      ) : (
                        <span className="thumb-question">
                          {insight.question || insight.title || 'Untitled'}
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="data-gallery__card-body">
                      <div className="data-gallery__card-title">
                        {insight.title || insight.question || 'Untitled Insight'}
                      </div>
                      {(insight.answer || insight.content) && (
                        <div className="data-gallery__card-preview">
                          {(insight.answer || insight.content).substring(0, 120)}
                        </div>
                      )}
                      <div className="data-gallery__card-footer">
                        <div className="data-gallery__card-tags">
                          {cType && (
                            <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>{cType}</Tag>
                          )}
                          {insight.tags?.slice(0, 2).map((t, i) => (
                            <Tag key={i} color="blue" style={{ fontSize: 11, margin: 0 }}>{t}</Tag>
                          ))}
                        </div>
                        <span className="data-gallery__card-date">
                          {insight.timestamp ? dayjs(insight.timestamp).format('MMM DD') : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {filteredAndSorted.length > PAGE_SIZE && (
              <div className="data-gallery__pagination">
                <Pagination
                  current={currentPage}
                  pageSize={PAGE_SIZE}
                  total={filteredAndSorted.length}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                />
              </div>
            )}
          </>
        )}
      </Spin>

      {/* Detail Drawer */}
      <Drawer
        title={drawerInsight?.title || drawerInsight?.component_title || drawerInsight?.question || 'Insight Details'}
        placement="right"
        width={560}
        open={!!drawerInsight}
        onClose={() => setDrawerInsight(null)}
        styles={{ body: { paddingTop: 12 } }}
      >
        {drawerInsight && <InsightDetail insight={drawerInsight} />}
      </Drawer>
    </>
  );

  /* ── Tab: RAG Setting ── */
  const renderRagSettingTab = () => (
    <div className="data-gallery__settings-tab">
      <Row gutter={24} className="data-gallery__settings-3col">
        {/* LEFT: Vector DB Stats */}
        <Col xs={24} md={7}>
          <div className="data-gallery__settings-panel">
            <div className="data-gallery__settings-panel-icon">
              <CloudServerOutlined style={{ fontSize: 56, color: 'var(--theme-primary)' }} />
            </div>
            <Title level={5} style={{ textAlign: 'center', marginTop: 12, marginBottom: 16 }}>Vector Database</Title>

            <div className="data-gallery__vdb-stat">
              <DatabaseOutlined style={{ fontSize: 18, color: 'var(--theme-primary)' }} />
              <div>
                <div className="data-gallery__vdb-stat-value">{totalCount}</div>
                <div className="data-gallery__vdb-stat-label">Total Indexed Items</div>
              </div>
            </div>

            <div className="data-gallery__vdb-stat">
              <CheckCircleOutlined style={{ fontSize: 18, color: '#52c41a' }} />
              <div>
                <div className="data-gallery__vdb-stat-value">
                  {galleryData?.insights?.filter(i => i.source_type === 'verified_answer').length || 0}
                </div>
                <div className="data-gallery__vdb-stat-label">Verified Answers</div>
              </div>
            </div>

            <div className="data-gallery__vdb-stat">
              <DashboardOutlined style={{ fontSize: 18, color: '#1890ff' }} />
              <div>
                <div className="data-gallery__vdb-stat-value">
                  {galleryData?.insights?.filter(i => i.source_type === 'dashboard_component').length || 0}
                </div>
                <div className="data-gallery__vdb-stat-label">Dashboard Components</div>
              </div>
            </div>

            <div className="data-gallery__vdb-stat">
              <ApiOutlined style={{ fontSize: 18, color: '#722ed1' }} />
              <div>
                <div className="data-gallery__vdb-stat-value">384</div>
                <div className="data-gallery__vdb-stat-label">Embedding Dimensions</div>
              </div>
            </div>

            <Divider style={{ margin: '12px 0' }} />
            <div style={{ textAlign: 'center' }}>
              <Tag color={indexerStatus?.status === 'error' ? 'red' : 'green'} style={{ fontSize: 12 }}>
                {indexerStatus?.status === 'error' ? 'Unhealthy' : 'Healthy'}
              </Tag>
            </div>
          </div>
        </Col>

        {/* MIDDLE: Indexing */}
        <Col xs={24} md={10}>
          <div className="data-gallery__settings-panel">
            <Title level={5} style={{ marginBottom: 12 }}>
              <SyncOutlined style={{ marginRight: 8 }} />
              Indexing
              {indexerStatus && (
                <Tag style={{ marginLeft: 8 }} color={
                  indexerStatus.status === 'running' ? 'processing' :
                  indexerStatus.status === 'error' ? 'error' :
                  indexerStatus.is_paused ? 'warning' : 'success'
                }>
                  {indexerStatus.is_paused ? 'Paused' : indexerStatus.status?.toUpperCase()}
                </Tag>
              )}
            </Title>

            {/* Progress bars */}
            <div className="data-gallery__indexer-progress">
              <div className="data-gallery__progress-item">
                <div className="data-gallery__progress-header">
                  <Text style={{ fontSize: 12 }}>Sync Status</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {indexerStatus?.status === 'running' ? 'In Progress...' : 'Complete'}
                  </Text>
                </div>
                <Progress
                  percent={indexerStatus?.status === 'running' ? 65 : 100}
                  status={indexerStatus?.status === 'running' ? 'active' : indexerStatus?.status === 'error' ? 'exception' : 'success'}
                  size="small"
                  strokeColor={indexerStatus?.status === 'running' ? 'var(--theme-primary)' : undefined}
                />
              </div>
              <div className="data-gallery__progress-item">
                <div className="data-gallery__progress-header">
                  <Text style={{ fontSize: 12 }}>Vector Embeddings</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {vectorRebuilding ? 'Rebuilding...' : `${totalCount} vectors`}
                  </Text>
                </div>
                <Progress
                  percent={vectorRebuilding ? 40 : 100}
                  status={vectorRebuilding ? 'active' : 'success'}
                  size="small"
                  strokeColor={vectorRebuilding ? '#722ed1' : undefined}
                />
              </div>
            </div>

            {/* Timing info */}
            <Row gutter={8} style={{ marginBottom: 12 }}>
              <Col span={8}>
                <div className="data-gallery__timing-card">
                  <Text type="secondary" style={{ fontSize: 11 }}>Last Run</Text>
                  <Text strong style={{ fontSize: 12 }}>
                    {indexerStatus?.last_run ? dayjs(indexerStatus.last_run).format('MM/DD HH:mm') : 'Never'}
                  </Text>
                </div>
              </Col>
              <Col span={8}>
                <div className="data-gallery__timing-card">
                  <Text type="secondary" style={{ fontSize: 11 }}>Next Run</Text>
                  <Text strong style={{ fontSize: 12 }}>
                    {indexerStatus?.is_paused ? 'Paused' : indexerStatus?.next_run ? dayjs(indexerStatus.next_run).format('MM/DD HH:mm') : 'N/A'}
                  </Text>
                </div>
              </Col>
              <Col span={8}>
                <div className="data-gallery__timing-card">
                  <Text type="secondary" style={{ fontSize: 11 }}>Last Result</Text>
                  <Text strong style={{ fontSize: 12 }}>
                    {indexerStatus?.last_summary
                      ? `+${indexerStatus.last_summary.items_added || 0} / -${indexerStatus.last_summary.items_removed || 0}` : 'N/A'}
                  </Text>
                </div>
              </Col>
            </Row>

            {/* Actions */}
            <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
              <Button type="primary" size="small" icon={<PlayCircleOutlined />} onClick={handleRunNow}
                loading={indexerLoading || indexerStatus?.status === 'running'}
                disabled={indexerStatus?.status === 'running'}>Sync & Rebuild</Button>
              <Button size="small" icon={<ThunderboltOutlined />} onClick={handleVectorRebuild}
                loading={vectorRebuilding || indexerStatus?.status === 'running'}
                disabled={indexerStatus?.status === 'running'}>Rebuild Vectors</Button>
              <Button size="small" icon={indexerStatus?.is_paused ? <CaretRightOutlined /> : <PauseCircleOutlined />}
                onClick={handlePauseResume} loading={indexerLoading}>
                {indexerStatus?.is_paused ? 'Resume' : 'Pause'}
              </Button>
              <Button size="small" icon={<ReloadOutlined />}
                onClick={() => { loadIndexerStatus(); loadIndexerHistory(); loadScheduleConfig(); }}>
                Refresh
              </Button>
            </Space>

            {/* Schedule */}
            <div style={{ marginBottom: 12 }}>
              <Space align="center">
                <Text type="secondary" style={{ fontSize: 12 }}>Schedule:</Text>
                <InputNumber min={5} max={1440} size="small" style={{ width: 110 }}
                  value={Math.round((scheduleConfig?.interval_seconds || 3600) / 60)}
                  onChange={(val) => val && handleSaveScheduleConfig(val * 60)}
                  disabled={scheduleSaving} addonAfter="min" />
              </Space>
            </div>

            {/* History table */}
            <Table size="small" dataSource={indexerHistory} rowKey="id"
              pagination={{ pageSize: 4, size: 'small' }}
              scroll={{ y: 160 }}
              columns={[
                { title: 'Time', dataIndex: 'started_at', width: 120,
                  render: v => v ? dayjs(v).format('MM/DD HH:mm') : '-' },
                { title: 'Dur', dataIndex: 'duration_seconds', width: 55,
                  render: v => v != null ? `${v.toFixed(1)}s` : '-' },
                { title: '+/-', width: 70,
                  render: (_, r) => <span><span style={{ color: '#52c41a' }}>+{r.items_added || 0}</span>{' / '}<span style={{ color: '#ff4d4f' }}>-{r.items_removed || 0}</span></span> },
                { title: 'Status', dataIndex: 'status', width: 70,
                  render: v => <Tag color={v === 'success' ? 'green' : v === 'running' ? 'blue' : 'red'}>{v}</Tag> },
              ]}
            />
          </div>
        </Col>

        {/* RIGHT: RAG Configuration */}
        <Col xs={24} md={7}>
          <div className="data-gallery__settings-panel">
            <div className="data-gallery__settings-panel-icon">
              <FileSearchOutlined style={{ fontSize: 56, color: '#722ed1' }} />
            </div>
            <Title level={5} style={{ textAlign: 'center', marginTop: 12, marginBottom: 16 }}>
              RAG Configuration
              {ragConfig && (
                <Tag style={{ marginLeft: 8 }} color={ragConfig.rag_enabled ? 'green' : 'default'}>
                  {ragConfig.rag_enabled ? 'ON' : 'OFF'}
                </Tag>
              )}
            </Title>

            {ragConfig ? (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div className="data-gallery__rag-field">
                  <div className="data-gallery__rag-field-header">
                    <Text strong style={{ fontSize: 13 }}>Enable RAG</Text>
                    <Switch checked={ragConfig.rag_enabled}
                      onChange={(c) => handleSaveRagConfig({ rag_enabled: c })}
                      loading={ragSaving} checkedChildren="ON" unCheckedChildren="OFF" size="small" />
                  </div>
                  <Text type="secondary" style={{ fontSize: 11 }}>Auto-search gallery before each query</Text>
                </div>

                <div className="data-gallery__rag-field">
                  <Text strong style={{ fontSize: 13 }}>Top K Results</Text>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Number of similar entries (1–20)</Text>
                  <InputNumber min={1} max={20} value={ragConfig.rag_top_k}
                    onChange={(v) => v && handleSaveRagConfig({ rag_top_k: v })}
                    disabled={ragSaving} style={{ width: '100%' }} size="small" />
                </div>

                <div className="data-gallery__rag-field">
                  <Text strong style={{ fontSize: 13 }}>Similarity Threshold</Text>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>Minimum score to include (0–1)</Text>
                  <Slider min={0} max={1} step={0.05} value={ragConfig.rag_similarity_threshold}
                    onChange={(v) => handleSaveRagConfig({ rag_similarity_threshold: v })}
                    disabled={ragSaving} marks={{ 0: '0', 0.5: '0.5', 1: '1' }} />
                </div>
              </Space>
            ) : <Spin size="small" />}
          </div>
        </Col>
      </Row>
    </div>
  );

  /* ── Tab: Q&A History ── */
  const renderQAHistoryTab = () => (
    <QAHistory
      agentName="Data Agent"
      fetchHistory={getDataQAHistory}
      fetchStatistics={getDataQAStatistics}
      submitFeedback={(qaId, feedback) => submitDataFeedback(qaId, feedback)}
      markAnswer={(qaId, isCorrect) => markDataQAAnswer(qaId, isCorrect)}
      hideHeader
    />
  );

  /* ── Build tab items ── */
  const tabItems = [
    {
      key: 'insights',
      label: <span><AppstoreOutlined /> All Insights</span>,
      children: renderInsightsTab(),
    },
    ...(isAdmin ? [{
      key: 'rag-setting',
      label: <span><SettingOutlined /> RAG Setting</span>,
      children: renderRagSettingTab(),
    }] : []),
    ...(isAdmin ? [{
      key: 'qa-history',
      label: <span><HistoryOutlined /> Q&A History</span>,
      children: renderQAHistoryTab(),
    }] : []),
  ];

  return (
    <div className="data-gallery">
      {/* Header */}
      <div className="data-gallery__header">
        <Title level={2} style={{ color: 'var(--theme-primary)', margin: 0 }}>Data Gallery</Title>
        <div className="data-gallery__header-actions">
          <Button icon={<ReloadOutlined />} onClick={loadGallery} loading={loading}>Refresh</Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className="data-gallery__tabs"
        destroyInactiveTabPane={false}
      />
    </div>
  );
}

/* ── Live chart that renders with actual fetched data ──────── */
function LiveChart({ spec, liveData }) {
  const ref = useRef(null);
  const parsed = (() => {
    if (!spec) return null;
    try { return typeof spec === 'string' ? JSON.parse(spec) : { ...spec }; } catch { return null; }
  })();
  const isPlotly = parsed && isPlotlyFigure(parsed);
  useEffect(() => {
    if (isPlotly) return;
    if (!ref.current || !parsed) return;
    try {
      // Inject live data into the spec
      if (liveData?.rows?.length > 0) {
        parsed.data = { values: liveData.rows };
      }
      const full = { ...parsed, width: 'container', height: 300 };
      vegaEmbed(ref.current, full, {
        actions: { export: true, source: false, compiled: false, editor: false }
      }).catch(() => {});
    } catch { /* ignore */ }
  }, [parsed, liveData, isPlotly]);
  if (isPlotly) {
    // Convert liveData {columns, rows: [obj|array]} into the {columns, rows: array[]} shape PlotlyFigureView expects.
    let tableData = null;
    if (liveData?.rows?.length > 0) {
      const cols = liveData.columns || Object.keys(liveData.rows[0] || {});
      const rows = liveData.rows.map((r) => Array.isArray(r) ? r : cols.map((c) => r[c]));
      tableData = { columns: cols, rows };
    }
    return (
      <div className="data-gallery__drawer-chart">
        <PlotlyFigureView figure={parsed} tableData={tableData} fill layoutOverride={{ height: 300 }} />
      </div>
    );
  }
  return <div ref={ref} className="data-gallery__drawer-chart" />;
}

/* ── Convert raw API rows (arrays) to objects ──────────────── */
function normalizeQueryResult(data) {
  if (!data) return data;
  const { columns, rows, ...rest } = data;
  if (!columns || !rows) return data;
  // If rows are already objects, return as-is
  if (rows.length > 0 && !Array.isArray(rows[0])) return data;
  // Zip arrays into objects
  const objectRows = rows.map(row =>
    Object.fromEntries(columns.map((col, i) => [col, row[i] ?? null]))
  );
  return { ...rest, columns, rows: objectRows, row_count: objectRows.length };
}

/* ── Live data table ───────────────────────────────────────── */
function LiveDataTable({ data }) {
  if (!data?.columns || !data?.rows) return null;
  const columns = data.columns.map(col => ({
    title: col,
    dataIndex: col,
    key: col,
    ellipsis: true,
    width: 150,
    render: v => v != null ? String(v) : '-',
  }));
  return (
    <Table
      size="small"
      dataSource={data.rows.map((r, i) => ({ ...r, _key: i }))}
      rowKey="_key"
      columns={columns}
      pagination={{ pageSize: 10, size: 'small' }}
      scroll={{ x: data.columns.length * 150 }}
      style={{ marginTop: 8 }}
    />
  );
}

/* ── Insight detail panel (used inside Drawer) ─────────────── */
function InsightDetail({ insight }) {
  const meta = sourceMeta(insight.source_type);
  const chart = getChartSpec(insight);
  const sql = insight.delivery?.sql || insight.sql_query || insight.generated_sql;

  const [liveData, setLiveData] = useState(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState(null);

  const handleRunQuery = async () => {
    if (!sql) return;
    setLiveLoading(true);
    setLiveError(null);
    try {
      const result = await executeSql(sql, 200, true);
      if (result?.data) {
        const normalized = normalizeQueryResult(result.data);
        setLiveData(normalized);
        message.success(`Fetched ${normalized.row_count ?? normalized.rows?.length ?? 0} rows${result.cached ? ' (cached)' : ''}`);
      } else {
        setLiveError('No data returned');
      }
    } catch (err) {
      setLiveError(err.message || 'Failed to execute query');
      message.error('Failed to run query');
    } finally {
      setLiveLoading(false);
    }
  };

  // Auto-run if opened via the card's run button
  useEffect(() => {
    if (insight._autoRun && sql && !liveData && !liveLoading) {
      handleRunQuery();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Tags row */}
      <Space wrap size={[8, 8]}>
        <Tag color={meta.color} style={{ fontSize: 12 }}>{meta.icon} {meta.full}</Tag>
        {inferComponentType(insight) && <Tag color="purple" style={{ fontSize: 12 }}>📈 {inferComponentType(insight)}</Tag>}
        {insight.component_id && <Tag color="blue" style={{ fontSize: 12 }}>🔖 {insight.component_id}</Tag>}
      </Space>

      {/* Run button */}
      {sql && (
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          onClick={handleRunQuery}
          loading={liveLoading}
          block
          className="data-gallery__run-btn"
        >
          {liveData ? 'Re-run with Live Data' : 'Run with Live Data'}
        </Button>
      )}

      {/* Live data error */}
      {liveError && (
        <Alert message={liveError} type="error" showIcon closable
          onClose={() => setLiveError(null)} style={{ fontSize: 12 }} />
      )}

      {/* Live data results */}
      {liveData && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">
            <Space>
              <span>Live Results</span>
              <Tag color="green" style={{ fontSize: 10 }}>FRESH</Tag>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {liveData.row_count ?? liveData.rows?.length ?? 0} rows · {liveData.columns?.length || 0} columns
              </Text>
            </Space>
          </div>

          {/* Render chart with live data if spec exists */}
          {chart && (
            <div style={{ marginBottom: 12 }}>
              <LiveChart spec={chart} liveData={liveData} />
            </div>
          )}

          {/* Metric display for single-value results */}
          {liveData.rows?.length === 1 && liveData.columns?.length <= 3 && (
            <Row gutter={12} style={{ marginBottom: 12 }}>
              {liveData.columns.map((col, i) => (
                <Col key={i} span={Math.floor(24 / liveData.columns.length)}>
                  <Card size="small" className="data-gallery__live-metric-card">
                    <Statistic
                      title={<Text style={{ fontSize: 11 }}>{col}</Text>}
                      value={liveData.rows[0][col]}
                      valueStyle={{ fontSize: 22, fontWeight: 700, color: 'var(--theme-primary)' }}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          )}

          {/* Data table */}
          <LiveDataTable data={liveData} />
        </div>
      )}

      {/* Dashboard source */}
      {insight.source_type === 'dashboard_component' && insight.dashboard_title && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">Source Dashboard</div>
          <div className="data-gallery__drawer-dashboard">
            <Space><DashboardOutlined style={{ color: '#1890ff' }} /><Text>{insight.dashboard_title}</Text></Space>
          </div>
        </div>
      )}

      {/* Question */}
      {insight.question && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">Question</div>
          <div className="data-gallery__drawer-question">{insight.question}</div>
        </div>
      )}

      {/* Answer */}
      {(insight.answer || insight.content) && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">{insight.answer ? 'Answer' : 'Content'}</div>
          <div className="data-gallery__drawer-answer">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{insight.answer || insight.content}</ReactMarkdown>
          </div>
        </div>
      )}

      {/* Chart (sample/original — hidden if live data is showing a chart) */}
      {chart && !liveData && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">Chart (Sample Data)</div>
          <DrawerChart spec={chart} />
        </div>
      )}

      {/* SQL */}
      {sql && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">SQL Query</div>
          <pre className="data-gallery__drawer-sql">{sql}</pre>
        </div>
      )}

      {/* Original delivery data (only show if no live data) */}
      {!liveData && insight.delivery?.data && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">Stored Data Results</div>
          <div className="data-gallery__drawer-data-card">
            {insight.delivery.data.columns && (
              <div><Text type="secondary" style={{ fontSize: 11 }}>Columns: {insight.delivery.data.columns.join(', ')}</Text></div>
            )}
            {insight.delivery.data.row_count !== undefined && (
              <div><Text type="secondary" style={{ fontSize: 11 }}>Rows: {insight.delivery.data.row_count}</Text></div>
            )}
            {insight.delivery.data.rows?.length > 0 && (
              <pre>{JSON.stringify(insight.delivery.data.rows.slice(0, 5), null, 2)}
                {insight.delivery.data.rows.length > 5 && '\n... and more'}</pre>
            )}
          </div>
        </div>
      )}

      {/* Confidence */}
      {insight.delivery?.confidence && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">Confidence</div>
          <Space>
            <Tag color={insight.delivery.confidence.score >= 0.8 ? 'green' : insight.delivery.confidence.score >= 0.6 ? 'orange' : 'red'}>
              {(insight.delivery.confidence.score * 100).toFixed(0)}%
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>{insight.delivery.confidence.reasoning}</Text>
          </Space>
        </div>
      )}

      {/* Description */}
      {insight.component_description && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">Description</div>
          <Text style={{ fontSize: 13 }}>{insight.component_description}</Text>
        </div>
      )}

      {/* Tags */}
      {insight.tags?.length > 0 && (
        <div className="data-gallery__drawer-section">
          <div className="data-gallery__drawer-section-label">Tags</div>
          <Space wrap>{insight.tags.map((t, i) => <Tag key={i} color="blue">{t}</Tag>)}</Space>
        </div>
      )}

      {/* Timestamp */}
      {insight.timestamp && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Created: {dayjs(insight.timestamp).format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      )}
    </Space>
  );
}

export default DataGallery;
